import { cookieIcon } from 'assets/svg'
import useMediaQuery from 'hooks/useMediaQuery'
import { useCallback, useEffect, useState } from 'react'
import { MOBILE_WIDTH } from 'utils/constants'
import * as Styles from './styles'
import { Button } from '@cannect/new-components/atoms'

const CONSENT_STORAGE_KEY = '@COOKIES_CONSENT'
const LEGACY_STORAGE_KEY = '@COOKIES_ACCEPTED'

type ConsentState = {
  analytics_storage: 'granted' | 'denied'
  ad_storage: 'granted' | 'denied'
  ad_user_data: 'granted' | 'denied'
  ad_personalization: 'granted' | 'denied'
}

const ALL_GRANTED: ConsentState = {
  analytics_storage: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted'
}

const ALL_DENIED: ConsentState = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied'
}

function updateGtagConsent(consent: ConsentState) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', consent)
  }
}

// Remove o banner pre-React do index.html quando a decisao vem por este
// componente, para nao deixar os dois na tela.
function dismissPreConsentBanner() {
  document.getElementById('pre-consent')?.remove()
}

function loadThirdPartyScripts() {
  // Clarity
  if (!document.getElementById('clarity-script')) {
    const script = document.createElement('script')
    script.id = 'clarity-script'
    script.textContent = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window,document,"clarity","script","r4n2mv74uo");
    `
    document.head.appendChild(script)
  }

  // Hotjar
  if (!document.getElementById('hotjar-script')) {
    const script = document.createElement('script')
    script.id = 'hotjar-script'
    script.textContent = `
      (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:5069610,hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `
    document.head.appendChild(script)
  }

  // Crazy Egg
  if (!document.getElementById('crazyegg-script')) {
    const script = document.createElement('script')
    script.id = 'crazyegg-script'
    script.async = true
    script.src = '//script.crazyegg.com/pages/scripts/0112/2863.js'
    document.head.appendChild(script)
  }
}

function CookiesConsent() {
  const [visible, setVisible] = useState(false)
  const isMobile = useMediaQuery(MOBILE_WIDTH)

  useEffect(() => {
    // A chave legada (@COOKIES_ACCEPTED) nao vale como decisao: quem a possui
    // nunca viu este banner nem escolheu por categoria, entao promove-la a
    // ALL_GRANTED era consentimento presumido. O banner pre-React no
    // index.html ja a remove; aqui cobrimos o caso de ele nao ter rodado.
    localStorage.removeItem(LEGACY_STORAGE_KEY)

    const applySaved = () => {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY)

      if (!saved) return false

      try {
        const consent = JSON.parse(saved) as ConsentState
        if (consent.analytics_storage === 'granted') {
          loadThirdPartyScripts()
        }
      } catch {
        /* ignore */
      }

      setVisible(false)

      return true
    }

    if (applySaved()) return undefined

    // Sem decisao salva o banner pre-React esta na tela. Ele resolve o
    // consentimento e emite o evento; aqui so carregamos os scripts de
    // terceiros e evitamos mostrar um segundo banner.
    const handleExternalConsent = () => {
      applySaved()
    }

    window.addEventListener('cannect:consent', handleExternalConsent)

    // Se o banner pre-React nao estiver na pagina (falha de script, HTML
    // antigo em cache), este componente assume e mostra o banner.
    if (!document.getElementById('pre-consent')) {
      setVisible(true)
    }

    return () => window.removeEventListener('cannect:consent', handleExternalConsent)
  }, [])

  const handleAcceptAll = useCallback(() => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(ALL_GRANTED))
    updateGtagConsent(ALL_GRANTED)
    loadThirdPartyScripts()
    dismissPreConsentBanner()
    setVisible(false)
  }, [])

  const handleRejectOptional = useCallback(() => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(ALL_DENIED))
    updateGtagConsent(ALL_DENIED)
    dismissPreConsentBanner()
    setVisible(false)
  }, [])

  if (!visible) return null

  return (
    <Styles.CookiesConsent id="cookies-consent" isMobile={isMobile}>
      <div className="cookie-bar">
        <div className="cookie-bar-content">
          <div className="header-cookies">
            <img src={cookieIcon} alt="cookie consent icon" width="59px" height="59px" />
            <h2>Esse site usa cookies</h2>
          </div>

          <p>
            Armazenamos dados temporariamente para melhorar a sua experiência de navegação. Ao utilizar nossos serviços,
            você concorda com tal monitoramento.
          </p>

          <div className="footer-cookies">
            <a href="https://www.cannect.life/politica-de-privacidade">Política de privacidade</a>
            <div className="cookie-actions">
              <Button onClick={handleRejectOptional} size="md" variant="outline" className="btn-reject">
                Rejeitar opcionais
              </Button>
              <Button onClick={handleAcceptAll} size="md" variant="success_light">
                Aceitar todos
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Styles.CookiesConsent>
  )
}

export default CookiesConsent
